import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import EmbedModal from './EmbedModal';

const { FiPlay, FiPause, FiVolume2, FiShare2 } = FiIcons;

const WidgetPlayer = ({ streamUrl, logoUrl, isEmbedded = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [metadata, setMetadata] = useState({ title: "Connecting...", artist: "On Air" });
  const [isLoading, setIsLoading] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);
  const audioRef = useRef(null);

  const fetchMetadata = () => {
    const callbackName = 'radiojar_cb_s2_' + Math.floor(Math.random() * 1000000);
    window[callbackName] = (data) => {
      if (data && (data.title || data.artist)) {
        setMetadata({ title: data.title || "Live Stream 2", artist: data.artist || "Console Radio" });
      }
      delete window[callbackName];
      const script = document.getElementById(callbackName);
      if (script) document.body.removeChild(script);
    };

    const script = document.createElement('script');
    script.id = callbackName;
    script.src = `https://www.radiojar.com/api/stations/98mxa4t2c0nwv/now_playing/?callback=${callbackName}&_=${Date.now()}`;
    script.async = true;
    document.body.appendChild(script);
  };

  useEffect(() => {
    fetchMetadata();
    const interval = setInterval(fetchMetadata, 15000);
    return () => clearInterval(interval);
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      setIsLoading(true);
      audioRef.current.play()
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    }
  };

  return (
    <div className="w-[320px] h-[360px] bg-white rounded-[2.5rem] shadow-2xl border border-gray-100 overflow-hidden flex flex-col mx-auto relative group">
      {/* Share Button - Always Visible on Main Site */}
      {!isEmbedded && (
        <button 
          onClick={() => setIsShareOpen(true)}
          className="absolute top-3 right-3 z-10 p-2 bg-white/90 backdrop-blur-md border border-gray-100 hover:bg-[#00AEEF] hover:text-white text-gray-400 rounded-xl transition-all shadow-sm"
        >
          <SafeIcon icon={FiShare2} className="text-sm" />
        </button>
      )}

      <div className="h-40 bg-gray-900 relative overflow-hidden shrink-0">
        <img src={logoUrl} alt="Stream 2" className={`w-full h-full object-cover transition-transform duration-[5000ms] ${isPlaying ? 'scale-110' : 'scale-100'}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 to-transparent opacity-60"></div>
        <div className="absolute bottom-3 left-4 flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${isPlaying ? 'bg-[#00AEEF] animate-pulse' : 'bg-white/40'}`}></div>
          <span className="text-[8px] font-black text-white uppercase tracking-[0.2em]">Stream 2 Live</span>
        </div>
      </div>

      <div className="flex-1 p-6 flex flex-col justify-between bg-white text-center">
        <div className="space-y-1">
          <AnimatePresence mode="wait">
            <motion.div key={metadata.title} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="px-2">
              <h2 className="text-lg font-black text-gray-900 leading-tight truncate">{metadata.title}</h2>
              <p className="text-[10px] font-bold text-[#00AEEF] uppercase tracking-wider truncate">{metadata.artist}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="flex items-center justify-between gap-4">
          <button onClick={togglePlay} className="w-16 h-16 rounded-[1.25rem] bg-[#00AEEF] text-white flex items-center justify-center shadow-lg shadow-blue-100 hover:scale-105 transition-all shrink-0">
            {isLoading ? <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : isPlaying ? <SafeIcon icon={FiPause} className="text-2xl" /> : <SafeIcon icon={FiPlay} className="text-2xl ml-1" />}
          </button>

          <div className="flex-1 space-y-2">
            <div className="flex justify-between text-[8px] font-black text-gray-400 uppercase tracking-widest px-1">
              <SafeIcon icon={FiVolume2} />
              <span>{Math.round(volume * 100)}%</span>
            </div>
            <input type="range" min="0" max="1" step="0.01" value={volume} onChange={(e) => {setVolume(parseFloat(e.target.value)); audioRef.current.volume = parseFloat(e.target.value);}} className="w-full h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#00AEEF]" />
          </div>
        </div>

        <div className="pt-3 border-t border-gray-50">
          <p className="text-[8px] font-black text-gray-300 uppercase tracking-[0.3em]">Console Radio Digital</p>
        </div>
      </div>

      <EmbedModal isOpen={isShareOpen} onClose={() => setIsShareOpen(false)} embedPath="/embed/stream-2" width="320px" height="360px" />
      <audio ref={audioRef} src={streamUrl} crossOrigin="anonymous" />
    </div>
  );
};

export default WidgetPlayer;